import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, useField } from "formik";
import { Download, XCircle } from "react-bootstrap-icons";
import { base64ToFile, imageFileToBase64 } from "../../../helpers";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

interface IUploadFileProps {
  name: string;
  accept: string;
  placeholder: string;
  className?: string;
  maxSizeKb: number;
  onChange?: (file: File | null) => void;
}

export default function UploadFile({
  name,
  accept,
  placeholder,
  className,
  maxSizeKb,
  onChange,
}: IUploadFileProps) {
  const { t } = useTranslation();
  const [field, , helpers] = useField(name);
  const addImageRef = useRef<HTMLInputElement>(null);
  const [errorSize, setErrorSize] = useState(false);
  const [base64Image, setBase64Image] = useState("");
  const [fileInfo, setFileInfo] = useState({ name: "", size: 0 });

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > maxSizeKb * 1024) {
        setErrorSize(true);
        return;
      } else {
        setErrorSize(false);
        setFileInfo({ name: file.name, size: file.size });
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setBase64Image(result);

        const fileNameWithoutExt = file.name
          ? file.name.replace(/\.[^/.]+$/, "")
          : name;

        const currentFile = base64ToFile(result, fileNameWithoutExt);
        onChange ? onChange(currentFile) : helpers.setValue(currentFile);
      };
      reader.readAsDataURL(file);
      event.target.value = "";
    }
  };

  const handleDeleteImage = () => {
    setBase64Image("");
    onChange ? onChange(null) : helpers.setValue(null);
  };

  useEffect(() => {
    if (base64Image.length) return;

    const file = field.value as File;
    imageFileToBase64(file).then((base64) => {
      setBase64Image(base64);
      setFileInfo({ name: file.name, size: file.size });
    });
  }, [field.value]);

  return (
    <div
      className={classNames({
        [className]: className,
      })}
    >
      {base64Image ? (
        <div
          className="rounded-2 border border-success bg-success-bg w-100 d-flex align-items-center justify-content-center position-relative"
          style={{ padding: "9px 12px" }}
        >
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: "35px", height: "35px" }}>
                <img
                  src={base64Image}
                  alt="image"
                  style={{
                    objectFit: "cover",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
              <div>
                <div>{fileInfo.name}</div>
                <div
                  className="text-info text-capitalize"
                  style={{ fontSize: 12 }}
                >
                  {fileInfo.size
                    ? `${(fileInfo.size / 1024).toFixed(0)} KB`
                    : null}
                </div>
              </div>
            </div>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ width: 32, height: 32 }}
              onClick={handleDeleteImage}
            >
              <XCircle className="fs-5" />
            </div>
          </div>
        </div>
      ) : (
        <div
          className="rounded-2 p-3 border border-success w-100 bg-success-bg text-success"
          onClick={() => addImageRef.current?.click()}
        >
          <div className="rounded-3 d-flex justify-content-center align-items-center cursor-pointer">
            <Download className="me-2" />
            {placeholder}
          </div>
        </div>
      )}

      {errorSize && (
        <div className="text-danger error-field">
          {t("firstStep.maxSize")} {maxSizeKb} KB
        </div>
      )}
      <ErrorMessage
        name={name}
        component="div"
        className="text-danger error-field"
      />
      <input
        ref={addImageRef}
        type="file"
        style={{ display: "none" }}
        accept={accept}
        onChange={handleImageChange}
      />
    </div>
  );
}
